import { useEffect, useState } from "react";
import { CalendarIcon } from "lucide-react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface DialogDatePickerProps {
  value: Date;
  onChange: (date: Date) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
}

export function DialogDatePicker({ value, onChange, label, className = "", disabled = false }: DialogDatePickerProps) {
  const [inputValue, setInputValue] = useState("");

  // Format date for input (YYYY-MM-DD for date input)
  const formatDateForInput = (date: Date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  // Update input when value changes
  useEffect(() => {
    setInputValue(formatDateForInput(value));
  }, [value]);

  // Parse input and update date
  const handleInputChange = (val: string) => {
    setInputValue(val);
    
    if (val) {
      const newDate = new Date(val);
      if (!isNaN(newDate.getTime())) {
        onChange(newDate);
      }
    }
  };

  return (
    <div className={cn("relative", className)}>
      {label && <Label className="text-xs text-muted-foreground mb-1.5 block">{label}</Label>}
      
      {/* Input with Calendar Icon */}
      <div className="relative">
        <input
          type="date"
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          className="w-full h-9 pl-2 pr-9 text-sm bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-9 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
          disabled={disabled}
        />
        
        {/* Calendar Icon inside input */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
          <CalendarIcon className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </div>
  );
}
